// LearnCore Frontend - 顶部导航栏组件

import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import ThemeToggle from './ThemeToggle';
import {
  BookOpenIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  ChevronDownIcon,
  ClockIcon,
  CheckCircleIcon,
  GiftIcon,
  CalendarIcon,
  UsersIcon,
  HomeIcon,
  DocumentTextIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';

interface TopNavigationProps {
  onMobileMenuToggle?: () => void;
}

export default function TopNavigation({ onMobileMenuToggle }: TopNavigationProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState<{ nickname?: string; avatar_url?: string } | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const toolsDropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('登出失败:', error);
    }
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const isToolsSubPath = () => {
    return location.pathname.startsWith('/flashcard') ||
           location.pathname.startsWith('/passages') ||
           location.pathname.startsWith('/classical-chinese');
  };

  useEffect(() => {
    let isMounted = true;
    const fetchSettings = async () => {
      if (!user) {
        setProfile(null);
        return;
      }
      try {
        const resp = await apiClient.getUserSettings();
        if (!isMounted) return;
        setProfile(resp.settings.profile || null);
      } catch (e) {
        // 忽略错误，使用邮箱兜底
      }
    };
    fetchSettings();
    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    // 自动关闭工具下拉菜单如果不是子路径
    if (!isToolsSubPath()) {
      setIsToolsDropdownOpen(false);
    }
  }, [location.pathname]);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(event.target as Node)) {
        setIsToolsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigationItems = [
    {
      name: '任务',
      path: '/tasks',
      icon: CheckCircleIcon,
      badge: null
    },
    {
      name: '奖励',
      path: '/rewards',
      icon: GiftIcon,
      badge: null
    },
    {
      name: '日历',
      path: '/calendar',
      icon: CalendarIcon,
      badge: null
    },
    {
      name: '学习市场',
      path: '/marketplace',
      icon: GlobeAltIcon,
      badge: null
    }
  ];

  const toolsSubItems = [
    {
      name: '学习集',
      path: '/flashcard',
      icon: HomeIcon
    },
    {
      name: '英文阅读',
      path: '/passages',
      icon: DocumentTextIcon
    },
    {
      name: '文言阅读',
      path: '/classical-chinese',
      icon: BookOpenIcon
    }
  ];

  return (
    <header className="top-navigation bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo和主导航 */}
          <div className="flex items-center">
            {/* 移动端菜单按钮 */}
            {onMobileMenuToggle && (
              <button
                onClick={onMobileMenuToggle}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 mr-3"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
            )}

            <Link
              to="/"
              className="flex items-center space-x-2 text-xl font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              <BookOpenIcon className="h-8 w-8" />
              <span className="hidden sm:inline">LearnCore</span>
            </Link>

            {/* 主要导航 */}
            {user && (
              <nav className="hidden md:flex items-center space-x-1 ml-6">
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`
                        flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                        ${isActivePath(item.path)
                          ? 'text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/20'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-800'
                        }
                      `}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="nav-text hidden lg:inline">{item.name}</span>
                      <span className="lg:hidden">{item.name.slice(0, 1)}</span>
                      {item.badge && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-600 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}

                {/* 学习工具下拉菜单 */}
                <div className="relative" ref={toolsDropdownRef}>
                  <button
                    onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
                    className={`
                      flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                      ${isToolsSubPath() || isToolsDropdownOpen
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }
                    `}
                  >
                    <BookOpenIcon className="h-4 w-4" />
                    <span className="nav-text hidden lg:inline">学习工具</span>
                    <span className="lg:hidden">工具</span>
                    <ChevronDownIcon className={`h-4 w-4 transition-transform ${
                      isToolsDropdownOpen ? 'rotate-180' : ''
                    }`} />
                  </button>

                  {/* 下拉菜单 */}
                  {isToolsDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                      <div className="py-1">
                        {toolsSubItems.map((subItem) => {
                          const Icon = subItem.icon;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setIsToolsDropdownOpen(false)}
                              className={`
                                flex items-center space-x-3 px-4 py-2 text-sm transition-colors
                                ${isActivePath(subItem.path)
                                  ? 'text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/20'
                                  : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                                }
                              `}
                            >
                              <Icon className="h-4 w-4" />
                              <span>{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* 家长管理入口 */}
                {user?.user_type !== 'child' && (
                  <Link
                    to="/parent"
                    className={`
                      flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                      ${isActivePath('/parent')
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }
                    `}
                  >
                    <UsersIcon className="h-4 w-4" />
                    <span className="nav-text hidden lg:inline">家长管理</span>
                    <span className="lg:hidden">家长</span>
                  </Link>
                )}
              </nav>
            )}
          </div>

          {/* 用户菜单和主题切换 */}
          <div className="flex items-center space-x-4">
            {/* 主题切换按钮 */}
            <ThemeToggle size="md" />
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-800 transition-colors"
                >
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt="avatar" className="h-8 w-8 rounded-full object-cover border" />
                  ) : (
                    <UserCircleIcon className="h-8 w-8" />
                  )}
                  <span className="hidden sm:block max-w-24 truncate">
                    {profile?.nickname || user.email}
                  </span>
                  <ChevronDownIcon className={`h-4 w-4 transition-transform ${
                    isUserMenuOpen ? 'rotate-180' : ''
                  }`} />
                </button>

                {/* 用户下拉菜单 */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    <div className="py-1">
                      <Link
                        to="/study-time"
                        onClick={() => setIsUserMenuOpen(false)}
                        className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                          isActivePath('/study-time')
                            ? 'text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/20'
                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                        }`}
                      >
                        <ClockIcon className="h-4 w-4" />
                        <span>学习时间</span>
                      </Link>

                      <Link
                        to="/user/center"
                        onClick={() => setIsUserMenuOpen(false)}
                        className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                          isActivePath('/user/center')
                            ? 'text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/20'
                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Cog6ToothIcon className="h-4 w-4" />
                        <span>设置</span>
                      </Link>

                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:text-red-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-red-400 dark:hover:bg-gray-700 transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        <span>登出</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors dark:text-gray-300 dark:hover:text-primary-400"
                >
                  登录
                </Link>
                <Link
                  to="/register"
                  className="btn-primary text-sm"
                >
                  注册
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}